import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { deviceTokenAPI } from '../services/api';
import {
  canRequestPushPermission,
  getPushPermissionState,
  setupIOSPushWithBackend,
  canUseVapidInNativeShell,
  isSubscribedToPush,
  unsubscribeFromPush,
  sendTestPushNotification,
} from '../utils/iosPush';
import {
  initializeFirebase,
  requestNotificationPermission as requestFcmPermission,
  onForegroundMessage,
  isNotificationSupported,
} from '../services/firebase';
import {
  isNativePushPlatform,
  getNativePlatform,
  ensureNativePushChannel,
  registerNativePush,
  addNativePushListeners,
  unregisterNativePush,
  NATIVE_PUSH_TOKEN_KEY,
} from '../services/nativePush';
import { useAuth } from './AuthContext';
import { queryKeys } from '../hooks/queries/queryKeys';

interface PushNotificationContextType {
  isSupported: boolean;
  permission: NotificationPermission | 'unsupported';
  isEnabled: boolean;
  isLoading: boolean;
  error: string | null;
  provider: 'vapid' | 'fcm' | 'native' | null;
  enablePushNotifications: () => Promise<boolean>;
  disablePushNotifications: () => Promise<void>;
  sendTestNotification: () => Promise<boolean>;
}

interface PushStatus {
  isSupported: boolean;
  permission: NotificationPermission | 'unsupported';
  isEnabled: boolean;
  provider: 'vapid' | 'fcm' | 'native' | null;
}

const DEFAULT_PUSH_STATUS: PushStatus = {
  isSupported: false,
  permission: 'default',
  isEnabled: false,
  provider: null,
};

/** Browser-capability + existing-subscription probe. No server call except
 * the implicit, unawaited initializeFirebase() -- everything else is local
 * browser API (Notification.permission, PushManager, localStorage). */
const PushNotificationContext = createContext<PushNotificationContextType | undefined>(undefined);

/**
 * Drop this WebView's VAPID subscription once a native token is registered, so
 * one device never receives both. isSubscribedToPush/unsubscribeFromPush await
 * navigator.serviceWorker.ready, which never resolves when no service worker is
 * registered (pwa.ts skips registration on native), so the whole step is
 * guarded by canUseVapidInNativeShell() and bounded by a timeout. Never throws.
 */
async function dropNativeShellVapid(): Promise<void> {
  try {
    if (!(await canUseVapidInNativeShell())) return;
    const work = (async () => {
      if (await isSubscribedToPush()) await unsubscribeFromPush();
    })();
    await Promise.race([work, new Promise<void>((resolve) => setTimeout(resolve, 3000))]);
  } catch {
    // Non-fatal: a duplicate alert is better than a failed enable.
  }
}

async function checkPushStatus(): Promise<PushStatus> {
  // Native (Capacitor/FCM) outranks everything else INSIDE the app shell. The
  // Android System WebView does not implement the Push API, so the VAPID probes
  // below are not merely redundant there — they can report a subscription that
  // never delivers. Checked first, and returned from, so that never happens.
  if (isNativePushPlatform()) {
    const storedNativeToken = localStorage.getItem(NATIVE_PUSH_TOKEN_KEY);
    if (storedNativeToken) {
      return { isSupported: true, permission: 'granted', isEnabled: true, provider: 'native' };
    }
    // No native token yet. Safety alerts still need a path: if this WebView can
    // really run VAPID (Push API + a registered service worker) and the user is
    // already subscribed, report that honestly instead of "not enabled".
    if (await canUseVapidInNativeShell()) {
      if (await isSubscribedToPush()) {
        return { isSupported: true, permission: 'granted', isEnabled: true, provider: 'vapid' };
      }
    }
    return { isSupported: true, permission: 'default', isEnabled: false, provider: null };
  }

  // VAPID Web Push is the primary system
  const vapidSupported = canRequestPushPermission();
  // FCM is fallback (needs Notification + serviceWorker but not necessarily PushManager)
  const fcmSupported = isNotificationSupported();

  const isSupported = vapidSupported || fcmSupported;
  const permission: NotificationPermission | 'unsupported' = isSupported
    ? getPushPermissionState()
    : 'unsupported';

  // Check if already subscribed via VAPID
  if (vapidSupported) {
    const subscribed = await isSubscribedToPush();
    if (subscribed) {
      return { isSupported, permission, isEnabled: true, provider: 'vapid' };
    }
  }

  // Initialize Firebase lazily for foreground message handling — fire and
  // forget, matching pre-migration (never awaited, never affects the
  // resolved status).
  if (fcmSupported) {
    initializeFirebase().catch(() => {});
  }

  // Check if already subscribed via FCM (localStorage token)
  const storedFcmToken = localStorage.getItem('fcmToken');
  if (storedFcmToken) {
    return { isSupported, permission, isEnabled: true, provider: 'fcm' };
  }

  return { isSupported, permission, isEnabled: false, provider: null };
}

export const PushNotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  // Check support and current subscription status on mount, AND re-check on
  // window focus (2026-08-15 reliability fix). staleTime stays Infinity
  // (this is a browser-capability + local PushManager probe, not
  // time-decaying server data) but refetchOnWindowFocus: 'always' forces the
  // refetch despite that -- plain `true` would still no-op against
  // Infinity-fresh data. Without this, a browser silently rotating/
  // invalidating the VAPID subscription (a real, observed failure mode) went
  // undetected for the PWA's entire session: isSubscribedToPush() reads the
  // actual navigator.serviceWorker PushManager state, not a cache, so
  // re-running it on focus is a genuine re-validation, not just a refetch
  // for its own sake. isEnabled flipping to false here is what lets the UI
  // (wherever it reads usePushNotification().isEnabled) prompt the user to
  // re-subscribe, instead of the first DM after subscription rot just
  // silently never arriving.
  const statusQuery = useQuery<PushStatus>({
    queryKey: queryKeys.push.status(),
    queryFn: checkPushStatus,
    staleTime: Infinity,
    refetchOnWindowFocus: 'always',
    // Pre-migration checkStatus() had no try/catch/retry at all.
    retry: false,
  });

  const status = statusQuery.data ?? DEFAULT_PUSH_STATUS;
  const { isSupported, permission, isEnabled, provider } = status;

  // Set up foreground message handler when user is logged in and has enabled notifications
  useEffect(() => {
    if (!user || !isEnabled) return;
    // Native shell has its own delivery path (effect below); the FCM Web SDK
    // handler here is browser-only and would never fire in the WebView anyway.
    if (isNativePushPlatform()) return;

    let unsubscribe: (() => void) | null = null;
    let cancelled = false;

    onForegroundMessage((payload) => {
      if (Notification.permission === 'granted') {
        const title = payload.notification?.title || 'Helm';
        const options = {
          body: payload.notification?.body || 'You have a new notification',
          icon: '/icons/icon-192.png',
          badge: '/icons/badge-72.png',
          tag: payload.data?.type || 'default',
          data: payload.data,
        };
        const notification = new Notification(title, options);
        notification.onclick = () => {
          window.focus();
          if (payload.data?.taskId) {
            window.location.href = `/tasks/${payload.data.taskId}`;
          }
          notification.close();
        };
      }
    }).then((unsub) => {
      if (cancelled) {
        if (unsub) unsub();
      } else {
        unsubscribe = unsub;
      }
    });

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
    };
  }, [user, isEnabled]);

  // Native push: refresh the FCM registration token on every app launch and
  // attach delivery handlers.
  //
  // Re-registering on launch is not redundant with enablePushNotifications().
  // FCM rotates registration tokens on its own schedule (app restore, data
  // clear, long inactivity), and the server only ever learns the token we last
  // POSTed. Without this, a rotated token leaves user_device_tokens holding a
  // stale value and every push to that device silently vanishes — the exact
  // failure mode the VAPID path already re-validates against on window focus.
  useEffect(() => {
    if (!user || !isNativePushPlatform()) return;

    let cancelled = false;
    let detach: (() => void) | null = null;

    (async () => {
      // Existing opted-in users installed before the channel existed still need
      // it created, or Android drops their notifications on arrival.
      await ensureNativePushChannel();
      if (cancelled) return;

      const stored = localStorage.getItem(NATIVE_PUSH_TOKEN_KEY);
      // Only re-register if the user already opted in; never prompt on launch.
      if (stored) {
        const fresh = await registerNativePush();
        if (cancelled) return;
        if (fresh && fresh !== stored) {
          try {
            await deviceTokenAPI.register(fresh, getNativePlatform(), getDeviceName());
            localStorage.setItem(NATIVE_PUSH_TOKEN_KEY, fresh);
            await dropNativeShellVapid();
          } catch (err: unknown) {
            console.error('[Push] Native token refresh failed:', err);
          }
        }
      }

      const unsub = await addNativePushListeners(
        () => {
          // Delivered while the app is foregrounded. Android already renders
          // the system notification via presentationOptions in
          // capacitor.config.json, so nothing to draw here.
        },
        (payload) => {
          const taskId = payload.data?.taskId;
          if (typeof taskId === 'string' || typeof taskId === 'number') {
            window.location.href = `/tasks/${taskId}`;
          }
        }
      );
      if (cancelled) {
        if (unsub) unsub();
      } else {
        detach = unsub;
      }
    })();

    return () => {
      cancelled = true;
      if (detach) detach();
    };
  }, [user]);

  // mutationFn mirrors the pre-migration try-VAPID-then-FCM function body
  // exactly, including the "both failed, don't throw" tail -- returns a
  // result object instead of touching state directly, so the wrapper below
  // can patch the shared status cache once.
  const enableMutation = useMutation({
    mutationFn: async (): Promise<{ success: boolean; provider: 'vapid' | 'fcm' | 'native' | null }> => {
      // Strategy: native first when running inside the Capacitor shell, then
      // VAPID (works on iOS + all browsers), then FCM Web SDK.

      // 0. Native push via Capacitor/FCM — the only provider that reaches the
      // Android app when it is fully closed. Must precede VAPID: see the note
      // in checkPushStatus about the WebView's missing Push API.
      if (isNativePushPlatform()) {
        const nativeToken = await registerNativePush();
        if (nativeToken) {
          await deviceTokenAPI.register(nativeToken, getNativePlatform(), getDeviceName());
          localStorage.setItem(NATIVE_PUSH_TOKEN_KEY, nativeToken);
          // One delivery path per device: the backend sends to VAPID
          // subscriptions AND FCM tokens, so a device holding both would get
          // every alert twice. Drop this WebView's VAPID subscription now that
          // native works. Other browsers of the same user are untouched.
          await dropNativeShellVapid();
          return { success: true, provider: 'native' };
        }
        // Native registration failed or was denied. Never fall through to the
        // FCM Web SDK (cannot work in the shell). VAPID is tried only where the
        // WebView really supports it (Push API + a registered service worker);
        // the Android System WebView normally does not, so there this stays an
        // honest failure. This keeps safety alerts reachable on any WebView
        // that can deliver them.
        if (await canUseVapidInNativeShell()) {
          const result = await setupIOSPushWithBackend('/api');
          if (result.success) {
            return { success: true, provider: 'vapid' };
          }
        }
        return { success: false, provider: null };
      }

      // 1. Try VAPID Web Push (primary)
      if (canRequestPushPermission()) {
        const result = await setupIOSPushWithBackend('/api');
        if (result.success) {
          return { success: true, provider: 'vapid' };
        }
        // If VAPID failed for a reason other than "not supported", report it
        if (result.error && !result.error.includes('not supported')) {
          console.warn('[Push] VAPID setup failed, trying FCM fallback:', result.error);
        }
      }

      // 2. Fall back to Firebase FCM
      try {
        const token = await requestFcmPermission();
        if (token) {
          const deviceName = getDeviceName();
          await deviceTokenAPI.register(token, 'web', deviceName);
          localStorage.setItem('fcmToken', token);
          return { success: true, provider: 'fcm' };
        }
      } catch (err: unknown) {
        console.error('[Push] FCM fallback failed:', err);
      }

      // Both failed
      return { success: false, provider: null };
    },
    // Pre-migration had no retry wrapper — a single attempt per call.
    retry: false,
  });

  const enablePushNotifications = useCallback(async (): Promise<boolean> => {
    setError(null);
    const result = await enableMutation.mutateAsync();
    if (result.success) {
      queryClient.setQueryData<PushStatus>(queryKeys.push.status(), (old: PushStatus | undefined) => ({
        isSupported: old?.isSupported ?? true,
        permission: 'granted',
        isEnabled: true,
        provider: result.provider,
      }));
      return true;
    }
    setError('Could not enable notifications. Permission may have been denied.');
    return false;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient]);

  const disableMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      // Unregister native push (no-op off-native)
      const storedNativeToken = localStorage.getItem(NATIVE_PUSH_TOKEN_KEY);
      if (storedNativeToken) {
        await unregisterNativePush();
        try {
          await deviceTokenAPI.unregister(storedNativeToken);
        } catch {
          // Non-fatal — mirrors the FCM branch below.
        }
        localStorage.removeItem(NATIVE_PUSH_TOKEN_KEY);
      }

      // Unsubscribe from VAPID
      await unsubscribeFromPush();

      // Unsubscribe from FCM
      const storedFcmToken = localStorage.getItem('fcmToken');
      if (storedFcmToken) {
        try {
          await deviceTokenAPI.unregister(storedFcmToken);
        } catch {
          // Non-fatal
        }
        localStorage.removeItem('fcmToken');
      }
    },
    retry: false,
  });

  const disablePushNotifications = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      await disableMutation.mutateAsync();
      queryClient.setQueryData<PushStatus>(queryKeys.push.status(), (old: PushStatus | undefined) => ({
        isSupported: old?.isSupported ?? false,
        permission: old?.permission ?? 'default',
        isEnabled: false,
        provider: null,
      }));
    } catch (err: unknown) {
      console.error('[Push] Disable failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to disable notifications');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient]);

  const isLoading = statusQuery.isLoading || enableMutation.isPending || disableMutation.isPending;

  const handleSendTest = useCallback(async (): Promise<boolean> => {
    if (provider === 'vapid') {
      return sendTestPushNotification('/api');
    }
    // FCM test would need a backend endpoint; for now, show a local notification
    if (Notification.permission === 'granted') {
      new Notification('Test Notification', {
        body: 'Push notifications are working!',
        icon: '/icons/icon-192.png',
      });
      return true;
    }
    return false;
  }, [provider]);

  return (
    <PushNotificationContext.Provider
      value={{
        isSupported,
        permission,
        isEnabled,
        isLoading,
        error,
        provider,
        enablePushNotifications,
        disablePushNotifications,
        sendTestNotification: handleSendTest,
      }}
    >
      {children}
    </PushNotificationContext.Provider>
  );
};

function getDeviceName(): string {
  if (isNativePushPlatform()) return getNativePlatform() === 'ios' ? 'Helm iOS App' : 'Helm Android App';
  const ua = navigator.userAgent;
  if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome';
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Edg')) return 'Edge';
  return 'Web Browser';
}

export const usePushNotifications = () => {
  const context = useContext(PushNotificationContext);
  if (context === undefined) {
    throw new Error('usePushNotifications must be used within a PushNotificationProvider');
  }
  return context;
};
